import { ForwardedRef, forwardRef } from 'react';
import { FocusScope } from 'react-aria';
import useContainerSize from '../../../hooks/useContainerSize';
import { ClickedMarker } from '../streetview.type';
import { getHouseholdsText } from '../utils';
import WardMarkerCard from '../components/wardMarkerCard';
import HouseholdMarkerContainer from './HouseholdMarkerContainer';

interface MarkerTooltipProps {
  clickedMarker: ClickedMarker | null;
  setClickedMarker: (marker: ClickedMarker | null) => void;
  noCardZoneHeight?: number;
}
// Keep small so the card is fully visible on small screens
const cardGap = 2;

const getVerticalShift = (cardTop: number, cardHeight: number, mapHeight: number, topLimit: number) => {
  if (!mapHeight) return 0;
  const shiftUp = Math.min(0, (mapHeight - cardGap) - (cardTop + cardHeight));
  return cardTop + shiftUp < topLimit ? topLimit - cardTop : shiftUp;
};

const MarkerTooltip = forwardRef((props: MarkerTooltipProps, ref: ForwardedRef<HTMLDialogElement>) => {
  const { clickedMarker, setClickedMarker, noCardZoneHeight = 0 } = props;
  const [contentRef, { height: cardHeight }] = useContainerSize<HTMLDivElement>();

  if (!clickedMarker?.object) {
    return null;
  }
  const {
    x, y, viewport, shouldOpenLeft, shouldOpenUp,
  } = clickedMarker;
  const cardTop = shouldOpenUp ? y - cardHeight : y;
  const verticalShift = getVerticalShift(cardTop, cardHeight, viewport?.height ?? 0, noCardZoneHeight + cardGap);
  const getCardLabel = () => {
    if (clickedMarker.kind === 'ward') return clickedMarker.object.properties.name;
    const { households } = clickedMarker.object;
    return households.length === 1 ? households[0].householdReference : getHouseholdsText(households.length);
  };
  return (
    <FocusScope contain>
      <dialog
        open
        id="tooltip-container"
        aria-label={getCardLabel()}
        aria-modal="true"
        tabIndex={-1}
        ref={ref}
        className="absolute m-0 p-0 border-0 bg-transparent"
        style={{
          left: `${x}px`,
          top: `${y + verticalShift}px`,
          // Pin at the corner, card towards the centre of the map
          transform: `translate(${shouldOpenLeft ? '-100%' : '0'}, ${shouldOpenUp ? '-100%' : '0'})`,
          pointerEvents: 'auto',
        }}
      >
        <div ref={contentRef}>
          {clickedMarker.kind === 'ward' && (
          <WardMarkerCard
            wardName={clickedMarker.object.properties.name}
            numberOfHouseholds={clickedMarker.object.properties.householdCount ?? '0'}
            setClickedMarker={setClickedMarker}
          />
          )}
          {clickedMarker.kind === 'household' && (
          <HouseholdMarkerContainer
            key={`${clickedMarker.object.coordinates.latitude},${clickedMarker.object.coordinates.longitude}`}
            households={clickedMarker.object.households}
            setClickedMarker={setClickedMarker}
          />
          )}
        </div>
      </dialog>
    </FocusScope>
  );
});
export default MarkerTooltip;
